import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import BannerContent from "./BannerContent";
import {
  announcementDismissKey,
  isAnnouncementActive,
} from "../../../helpers/enterpriseAnnouncement";

const readDismissed = (key) => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

/**
 * Site-wide text bar authored by the enterprise admin.
 * Dismiss is per user + announcement version (localStorage) so a new
 * message or a re-enable shows the bar again.
 */
const EnterpriseAnnouncementBanner = () => {
  const user = useSelector((state) => state?.currentUser?.user);
  const banner = useSelector(
    (state) => state?.currentUser?.enterprise?.announcementBanner
  );

  const enterpriseId = user?.enterpriseId;
  const uid = user?.uid;
  const version = banner?.version;

  const storageKey = useMemo(
    () => announcementDismissKey({ enterpriseId, uid, version }),
    [enterpriseId, uid, version]
  );

  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  useEffect(() => {
    setDismissed(readDismissed(storageKey));
  }, [storageKey]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      // Private-mode Safari can throw; in-memory dismiss still holds.
    }
  }, [storageKey]);

  const active = isAnnouncementActive(banner);
  const visible = active && !dismissed;

  // Sit below the fixed 60px topbar. `.page-content` already uses
  // `margin-top: 60px` for that clearance; without this class the bar
  // occupies that gap and is painted *under* the topbar (invisible on
  // desktop, including while accessing a seller).
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-riverr-global-banner");
    return () => document.body.classList.remove("has-riverr-global-banner");
  }, [visible]);

  if (!visible) return null;

  const severity = banner.severity === "warning" ? "warning" : "info";

  return (
    <BannerContent
      message={banner.message}
      severity={severity}
      onClose={dismiss}
      className="riverr-announcement-bar--chrome"
    />
  );
};

export default EnterpriseAnnouncementBanner;
